import React, { useState, useEffect, useCallback } from 'react';
import { ResultsDisplay } from './JointApplicationForm';
import {
	CommercialApplicationData,
	CommercialApplicationResponse,
	CommercialPrimaryApplicant,
	CommercialProperty,
	CommercialRequestedFinancialParameters,
	CommercialProductTypeEnum,
	RegionEnum,
	EducationalLevelEnum,
	SourceOfIncomeEnum,
	Expenses,
	GenderEnum,
	MaritalStatusEnum,
	ResidentialTypeEnum,
	PersonaEnum,
	SectorEnum,
	JointApplicationResponse,
	OwnershipTypeEnum,
	RentFrequencyEnum,
	PropertyRentUnit,
} from './types';
import { apiFetch } from './lib/api';

// ==================== CONSTANTS ====================
const FORM_OPTIONS = {
	GENDER: Object.values(GenderEnum),
	MARITAL_STATUS: Object.values(MaritalStatusEnum),
	RESIDENTIAL_TYPE: Object.values(ResidentialTypeEnum),
	PERSONA: Object.values(PersonaEnum),
	SECTOR: Object.values(SectorEnum),
	COMMERCIAL_PRODUCT_TYPE: Object.values(CommercialProductTypeEnum),
	REGION: Object.values(RegionEnum),
	EDUCATIONAL_LEVEL: Object.values(EducationalLevelEnum),
	SOURCE_OF_INCOME: Object.values(SourceOfIncomeEnum),
	OWNERSHIP_TYPE: Object.values(OwnershipTypeEnum),
	RENT_FREQUENCY: Object.values(RentFrequencyEnum),
};

const INITIAL_EXPENSES: Expenses = {
	rent: 0,
	foodAndBeverages: 3000,
	education: 5000,
	healthCare: 1500,
	utilities: 1200,
	transportation: 2000,
	insurance: 1000,
	futureExpenses: 2000,
	others: 1500,
};

const INITIAL_PRIMARY_APPLICANT: CommercialPrimaryApplicant = {
	nationalId: '',
	nationality: 'SA',
	profileId: 'profile-001',
	gender: GenderEnum.Male,
	firstName: '',
	lastName: '',
	mobileNumber: '+966500000001',
	personalInformation: {
		dateOfBirth: {
			gregorian: '1985-03-15',
			hijri: '1405-07-23',
		},
	},
	maritalStatus: MaritalStatusEnum.Married,
	residentialType: ResidentialTypeEnum.HomeOwner,
	numberOfDependents: 3,
	numberOfDependentsInSchool: 1,
	numberOfCarsOwned: 2,
	expenses: { ...INITIAL_EXPENSES },
	sector: SectorEnum.SelfEmployed,
	persona: PersonaEnum.SELF_EMPLOYED,
	educationalLevel: EducationalLevelEnum.BACHELORS,
	sourceOfIncome: [SourceOfIncomeEnum.SELF_EMPLOYED, SourceOfIncomeEnum.REAL_ESTATE_YIELD],
	rentalAssignment: true,
	rePortfolioSize: 4,
};

const INITIAL_PROPERTY: CommercialProperty = {
	propertyId: 'prop-commercial-001',
	productType: CommercialProductTypeEnum.COMMERCIAL_STANDARD,
	price: 5000000,
	region: RegionEnum.RIYADH,
	propertyRentUnits: [],
};

const INITIAL_FINANCIAL_PARAMS: CommercialRequestedFinancialParameters = {
	downPayment: 1500000,
	tenor: 180,
	ownershipPercentage: 100,
};

const createEmptyRentUnit = (): PropertyRentUnit => ({
	startDate: new Date().toISOString().slice(0, 10),
	endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().slice(0, 10),
	income: 0,
	frequency: RentFrequencyEnum.MONTHLY,
	totalAnnualIncome: 0,
});

// ==================== UTILITY COMPONENTS ====================
const InputField: React.FC<{
	label: string;
	type: string;
	value: any;
	onChange: (value: any) => void;
	options?: string[];
	disabled?: boolean;
	placeholder?: string;
}> = ({ label, type, value, onChange, options, disabled = false, placeholder }) => (
	<div>
		<label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
		{type === 'select' && options ? (
			<select
				value={value}
				onChange={(e) => onChange(e.target.value)}
				disabled={disabled}
				className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 text-sm">
				{options.map((opt) => (
					<option key={opt} value={opt}>
						{opt}
					</option>
				))}
			</select>
		) : (
			<input
				type={type}
				value={value}
				onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
				disabled={disabled}
				placeholder={placeholder}
				className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 text-sm"
			/>
		)}
	</div>
);

const formatCurrency = (amount: number) => {
	if (amount === undefined || amount === null) return 'SAR 0';
	return new Intl.NumberFormat('en-SA', {
		style: 'currency',
		currency: 'SAR',
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(amount);
};

const formatPercentage = (value: number) => {
	if (value === undefined || value === null) return '0%';
	return `${value.toFixed(2)}%`;
};

// ==================== INSTALLMENT LABEL HELPER ====================
// The dominant rent frequency (the one holding the most total annual income)
// determines the billing cadence of financialParameters.equatedMonthlyInstallment:
//   MONTHLY               -> monthly installment (EMI)
//   QUARTERLY or longer   -> quarterly installment (EQI = EMI x 3)
const getInstallmentLabel = (rentUnits: PropertyRentUnit[]): string => {
	const today = new Date().toISOString().slice(0, 10);
	const activeUnits = rentUnits.filter((u) => u.endDate >= today);
	if (activeUnits.length === 0) return 'Monthly EMI';

	const totalsByFrequency = activeUnits.reduce<Record<string, number>>((acc, unit) => {
		acc[unit.frequency] = (acc[unit.frequency] || 0) + (unit.totalAnnualIncome || 0);
		return acc;
	}, {});

	let dominant: RentFrequencyEnum = RentFrequencyEnum.MONTHLY;
	let max = -1;
	(Object.entries(totalsByFrequency) as [RentFrequencyEnum, number][]).forEach(([freq, total]) => {
		if (total > max) {
			max = total;
			dominant = freq;
		}
	});

	return dominant === RentFrequencyEnum.MONTHLY ? 'Monthly EMI' : 'Quarterly Installment';
};

// ==================== COMMERCIAL INITIAL RESPONSE DISPLAY ====================
const CommercialResponseDisplay: React.FC<{ response: CommercialApplicationResponse; installmentLabel: string }> = ({
	response,
	installmentLabel,
}) => {
	const fp = response.financialParameters;
	const isEligible = response.eligibility.status === 'ELIGIBLE';

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<div className="flex items-center justify-between mb-4">
				<h3 className="text-lg font-semibold text-gray-800">Commercial Application Response</h3>
				<span
					className={`px-3 py-1 rounded-full text-xs font-semibold ${
						isEligible
							? 'bg-green-100 text-green-800'
							: 'bg-red-100 text-red-800'
					}`}>
					{response.eligibility.status}
				</span>
			</div>

			<div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
				<div className="text-xs">
					<span className="text-gray-500">Application ID:</span>
					<div className="font-mono font-medium text-blue-600 text-sm truncate" title={response.applicationId}>
						{response.applicationId}
					</div>
				</div>
				<div className="text-xs">
					<span className="text-gray-500">Ownership Type:</span>
					<div className="font-medium text-sm">{response.ownershipType}</div>
				</div>
				{fp && (
					<div className="text-xs">
						<span className="text-gray-500">Contract Type:</span>
						<div className="font-medium text-sm">{fp.contractType}</div>
					</div>
				)}
			</div>

			{/* NOT ELIGIBLE - Show ineligibility details */}
			{!isEligible && response.eligibility.ineligibilityType && (
				<div className={`border rounded-lg p-4 mb-4 ${
					response.eligibility.ineligibilityType === 'PERMANENT'
						? 'bg-red-50 border-red-200'
						: 'bg-yellow-50 border-yellow-200'
				}`}>
					<div className="flex items-center gap-2 mb-2">
						<span className={`px-2 py-1 rounded text-xs font-bold ${
							response.eligibility.ineligibilityType === 'PERMANENT'
								? 'bg-red-100 text-red-800'
								: 'bg-yellow-100 text-yellow-800'
						}`}>
							{response.eligibility.ineligibilityType}
						</span>
						<span className={`text-sm font-semibold ${
							response.eligibility.ineligibilityType === 'PERMANENT'
								? 'text-red-800'
								: 'text-yellow-800'
						}`}>
							Ineligibility
						</span>
					</div>
					{response.eligibility.ineligibilityReason && (
						<div className="mt-2">
							<div className="flex items-start gap-2">
								<span className={`text-xs font-mono px-1.5 py-0.5 rounded ${
									response.eligibility.ineligibilityType === 'PERMANENT'
										? 'bg-red-100 text-red-700'
										: 'bg-yellow-100 text-yellow-700'
								}`}>
									{response.eligibility.ineligibilityReason.code}
								</span>
							</div>
							<p className={`mt-1 text-sm ${
								response.eligibility.ineligibilityType === 'PERMANENT'
									? 'text-red-700'
									: 'text-yellow-700'
							}`}>
								{response.eligibility.ineligibilityReason.message}
							</p>
						</div>
					)}
				</div>
			)}

			{/* NOT ELIGIBLE without detailed reason */}
			{!isEligible && !response.eligibility.ineligibilityType && (
				<div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
					<p className="text-sm text-red-700 font-medium">Application is not eligible.</p>
				</div>
			)}

			{/* ELIGIBLE - Show financial parameters */}
			{fp && (
				<>
					<h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase border-t pt-3">Financial Parameters</h4>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						<div className="bg-blue-50 p-3 rounded text-center">
							<div className="text-xs text-gray-500 mb-1">Finance Amount</div>
							<div className="text-lg font-bold text-blue-700">{formatCurrency(fp.financeAmount)}</div>
						</div>
						<div className="bg-green-50 p-3 rounded text-center">
							<div className="text-xs text-gray-500 mb-1">{installmentLabel}</div>
							<div className="text-lg font-bold text-green-700">{formatCurrency(fp.equatedMonthlyInstallment)}</div>
						</div>
						<div className="bg-purple-50 p-3 rounded text-center">
							<div className="text-xs text-gray-500 mb-1">Tenure</div>
							<div className="text-lg font-bold text-purple-700">{fp.tenure} months</div>
						</div>
						<div className="bg-orange-50 p-3 rounded text-center">
							<div className="text-xs text-gray-500 mb-1">Property Price</div>
							<div className="text-lg font-bold text-orange-700">{formatCurrency(fp.propertyPrice)}</div>
						</div>
						<div className="bg-gray-50 p-3 rounded text-center">
							<div className="text-xs text-gray-500 mb-1">Min Down Payment</div>
							<div className="text-lg font-bold text-gray-700">{formatCurrency(fp.minimumDownPayment)}</div>
						</div>
						<div className="bg-gray-50 p-3 rounded text-center">
							<div className="text-xs text-gray-500 mb-1">Min Down Payment %</div>
							<div className="text-lg font-bold text-gray-700">{fp.minimumDownPaymentPercent}%</div>
						</div>
						<div className="bg-indigo-50 p-3 rounded text-center">
							<div className="text-xs text-gray-500 mb-1">Annual Profit Rate</div>
							<div className="text-lg font-bold text-indigo-700">{formatPercentage(fp.annualProfitRate)}</div>
						</div>
						<div className="bg-teal-50 p-3 rounded text-center">
							<div className="text-xs text-gray-500 mb-1">Murabaha Rate</div>
							<div className="text-lg font-bold text-teal-700">{formatPercentage(fp.murabahaRate)}</div>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

// ==================== COMMERCIAL OPEN BANKING SECTION ====================
const CommercialOpenBankingSection: React.FC<{ stage: any; application: any }> = ({ stage, application }) => {
	const data = stage?.outputResult?.data;
	if (!data) return null;

	const applicantResults = data.applicantResults || {};

	const getApplicantName = (nationalId: string) => {
		if (application.primaryApplicant.nationalId === nationalId) {
			const p = application.primaryApplicant.personalInformation;
			return `${p.firstName} ${p.familyName || p.lastName} (Primary)`;
		}
		return nationalId;
	};

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Commercial Open Banking Analysis</h3>

			{/* Aggregated Summary */}
			<div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
				<div className="bg-green-50 p-3 rounded text-center">
					<div className="text-xs text-gray-500 mb-1">Aggregated Combined Income</div>
					<div className="text-lg font-bold text-green-700">{formatCurrency(data.aggregatedCombinedMonthlyIncome || 0)}</div>
				</div>
				<div className="bg-blue-50 p-3 rounded text-center">
					<div className="text-xs text-gray-500 mb-1">Self-Employed Income</div>
					<div className="text-lg font-bold text-blue-700">{formatCurrency(data.aggregatedSelfEmployedMonthlyIncome || 0)}</div>
				</div>
				<div className="bg-purple-50 p-3 rounded text-center">
					<div className="text-xs text-gray-500 mb-1">Real Estate Yield Income</div>
					<div className="text-lg font-bold text-purple-700">{formatCurrency(data.aggregatedRealEstateYieldMonthlyIncome || 0)}</div>
				</div>
				<div className="bg-orange-50 p-3 rounded text-center">
					<div className="text-xs text-gray-500 mb-1">Undeclared Obligations</div>
					<div className="text-lg font-bold text-orange-700">{formatCurrency(data.totalUndeclaredObligations || 0)}</div>
				</div>
			</div>

			{/* Per-Applicant Results */}
			<div className="space-y-4">
				{Object.entries(applicantResults).map(([nationalId, result]: [string, any]) => (
					<div key={nationalId} className="border rounded-lg p-4 bg-gray-50">
						<div className="flex items-center justify-between mb-3">
							<h4 className="font-semibold text-gray-800">{getApplicantName(nationalId)}</h4>
							<span
								className={`px-2 py-1 rounded text-xs font-bold ${
									result.passesFoundationalRules ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
								}`}>
								{result.passesFoundationalRules ? 'PASSES RULES' : 'FAILS RULES'}
							</span>
						</div>

						{/* Income Breakdown */}
						<div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
							{result.selfEmployedIncome && (
								<div className="bg-white p-3 rounded border text-center">
									<div className="text-xs text-gray-500">Self-Employed Monthly Avg</div>
									<div className="text-lg font-bold text-blue-600">{formatCurrency(result.selfEmployedIncome.monthlyAverage)}</div>
									<div className="text-xs text-gray-400">
										{result.selfEmployedIncome.validTransactionCount} valid txns
									</div>
								</div>
							)}
							{result.realEstateYieldIncome && (
								<div className="bg-white p-3 rounded border text-center">
									<div className="text-xs text-gray-500">RE Yield Monthly Avg</div>
									<div className="text-lg font-bold text-purple-600">{formatCurrency(result.realEstateYieldIncome.monthlyAverage)}</div>
									<div className="text-xs text-gray-400">
										{result.realEstateYieldIncome.validTransactionCount} valid txns
									</div>
								</div>
							)}
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Combined Verified Monthly</div>
								<div className="text-lg font-bold text-green-600">{formatCurrency(result.combinedVerifiedMonthlyIncome)}</div>
							</div>
						</div>

						{/* Statement Vintage */}
						{result.statementVintage && (
							<div className="mb-4">
								<h5 className="text-sm font-semibold text-gray-600 mb-2">Statement Vintage</h5>
								<div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
									<div className="bg-white p-2 rounded border">
										<div className="text-gray-500">Total Months</div>
										<div className="font-semibold">{result.statementVintage.totalMonths}</div>
									</div>
									<div className="bg-white p-2 rounded border">
										<div className="text-gray-500">Zero Credit Months</div>
										<div className="font-semibold">{result.statementVintage.zeroCreditsMonths}</div>
									</div>
									<div className="bg-white p-2 rounded border">
										<div className="text-gray-500">Min History</div>
										<span className={`px-2 py-0.5 rounded text-xs font-bold ${result.statementVintage.passesMinimumHistory ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
											{result.statementVintage.passesMinimumHistory ? 'PASS' : 'FAIL'}
										</span>
									</div>
									<div className="bg-white p-2 rounded border">
										<div className="text-gray-500">Cash Flow Consistency</div>
										<span className={`px-2 py-0.5 rounded text-xs font-bold ${result.statementVintage.passesCashFlowConsistency ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
											{result.statementVintage.passesCashFlowConsistency ? 'PASS' : 'FAIL'}
										</span>
									</div>
								</div>
							</div>
						)}

						{/* Liquidity Buffer */}
						{result.liquidityBuffer && (
							<div className="mb-4">
								<div className="bg-white p-2 rounded border text-xs inline-block">
									<span className="text-gray-500">Avg Month-End Closing Balance: </span>
									<span className="font-semibold text-blue-700">{formatCurrency(result.liquidityBuffer.avgMonthEndClosingBalance)}</span>
								</div>
							</div>
						)}

						{/* Flags */}
						{result.flags && (
							<div className="mb-4">
								<h5 className="text-sm font-semibold text-gray-600 mb-2">Flags</h5>
								<div className="flex gap-2 flex-wrap text-xs">
									<span className={`px-2 py-1 rounded ${result.flags.velocitySpike ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
										Velocity Spike: {result.flags.velocitySpike ? 'YES' : 'NO'}
									</span>
									<span className="px-2 py-1 rounded bg-gray-100 text-gray-700">
										Window Dressing Excluded: {formatCurrency(result.flags.windowDressingExcludedAmount || 0)}
									</span>
								</div>
							</div>
						)}

						{/* Flagged Transactions */}
						{result.flaggedTransactions && result.flaggedTransactions.length > 0 && (
							<details className="bg-white rounded border p-2 text-xs">
								<summary className="cursor-pointer font-medium text-gray-700">
									Flagged Transactions ({result.flaggedTransactions.length})
								</summary>
								<div className="mt-2 overflow-x-auto">
									<table className="min-w-full text-xs text-left">
										<thead className="bg-gray-100 text-gray-600 uppercase font-medium">
											<tr>
												<th className="p-2 border-b">Description</th>
												<th className="p-2 border-b">Amount</th>
												<th className="p-2 border-b">Date</th>
												<th className="p-2 border-b">Reason</th>
											</tr>
										</thead>
										<tbody className="divide-y divide-gray-200">
											{result.flaggedTransactions.map((txn: any, idx: number) => (
												<tr key={idx} className="hover:bg-gray-50">
													<td className="p-2">{txn.description}</td>
													<td className="p-2 font-mono">{formatCurrency(txn.amount)}</td>
													<td className="p-2">{new Date(txn.date).toLocaleDateString()}</td>
													<td className="p-2">
														<span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
															txn.reason === 'CONTRA_ENTRY' ? 'bg-yellow-100 text-yellow-800' :
															txn.reason === 'SELF_TRANSFER' ? 'bg-blue-100 text-blue-800' :
															'bg-gray-100 text-gray-800'
														}`}>
															{txn.reason}
														</span>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</details>
						)}
					</div>
				))}
			</div>
		</div>
	);
};

// ==================== COMMERCIAL RAC POLICY SECTION ====================
const CommercialRacPolicySection: React.FC<{ stage: any }> = ({ stage }) => {
	const data = stage?.outputResult?.data;
	if (!data) return null;

	const policies = data.policies || {};
	const applicantRACResults = data.applicantRACResults || {};

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Commercial RAC Policy</h3>

			{data.mainApplicantPersona && (
				<div className="mb-4">
					<span className="text-sm text-gray-500">Main Applicant Persona: </span>
					<span className="px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs font-semibold">{data.mainApplicantPersona}</span>
				</div>
			)}

			{/* Policies */}
			{Object.entries(policies).length > 0 && (
				<div className="mb-6">
					<h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase">Applied Policies</h4>
					<div className="space-y-3">
						{Object.entries(policies).map(([key, policy]: [string, any]) => (
							<div key={key} className="border rounded-lg p-4 bg-gray-50">
								<h5 className="font-medium text-gray-800 mb-3 text-sm">{key}</h5>
								<div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
									<div>
										<div className="text-xs text-gray-500">Persona / Sector</div>
										<div className="font-semibold">{policy.persona} / {policy.sector}</div>
									</div>
									<div>
										<div className="text-xs text-gray-500">Initial Rating</div>
										<div className="font-semibold text-green-600">{policy.initialRating}</div>
									</div>
									<div>
										<div className="text-xs text-gray-500">Initial Pricing</div>
										<div className="font-semibold">{policy.initialPricing}%</div>
									</div>
									<div>
										<div className="text-xs text-gray-500">Min Income</div>
										<div className="font-semibold">{formatCurrency(policy.minIncome)}</div>
									</div>
									<div>
										<div className="text-xs text-gray-500">Age Range</div>
										<div className="font-semibold">{policy.minAge} - {policy.maxAgeAtMaturity} years</div>
									</div>
									<div>
										<div className="text-xs text-gray-500">Max DBR</div>
										<div className="font-semibold">{policy.maxDbr}%</div>
									</div>
									<div>
										<div className="text-xs text-gray-500">Max LTV</div>
										<div className="font-semibold">{policy.maxLtv}%</div>
									</div>
									<div>
										<div className="text-xs text-gray-500">Contract Type</div>
										<div className="font-semibold">{policy.contractType}</div>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Per-Applicant RAC Results */}
			{Object.entries(applicantRACResults).length > 0 && (
				<div>
					<h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase">Applicant RAC Results</h4>
					<div className="space-y-3">
						{Object.entries(applicantRACResults).map(([nationalId, result]: [string, any]) => (
							<div key={nationalId} className="border rounded-lg p-4 bg-gray-50">
								<h5 className="font-medium text-gray-800 mb-3 text-sm">{nationalId}</h5>
								<div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm mb-3">
									<div>
										<div className="text-xs text-gray-500">Selected Persona</div>
										<div className="font-semibold">{result.selectedPersona}</div>
									</div>
									<div>
										<div className="text-xs text-gray-500">Selection Method</div>
										<div className="font-semibold text-xs">{result.selectionMethod}</div>
									</div>
									<div>
										<div className="text-xs text-gray-500">Total Monthly Income</div>
										<div className="font-semibold text-green-700">{formatCurrency(result.totalVerifiedMonthlyIncome)}</div>
									</div>
								</div>

								{/* Income Breakdown */}
								{result.incomeBreakdown && result.incomeBreakdown.length > 0 && (
									<div>
										<div className="text-xs font-semibold text-gray-500 mb-2">Income Breakdown</div>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
											{result.incomeBreakdown.map((source: any, idx: number) => (
												<div key={idx} className="flex justify-between items-center p-2 bg-white rounded border text-xs">
													<span className="font-medium text-gray-700">{source.source}</span>
													<div className="text-right">
														<div className="font-semibold">{formatCurrency(source.amount)}</div>
														<div className="text-gray-400">{formatPercentage(source.percentage)}</div>
													</div>
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

// ==================== UNIFIED INCOME SECTION ====================
const UnifiedIncomeSection: React.FC<{ stage: any }> = ({ stage }) => {
	const data = stage?.outputResult?.data;
	if (!data) return null;

	const applicantIncomes = data.applicantIncomes || {};

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Unified Income</h3>

			<div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
				<div className="bg-green-50 p-3 rounded text-center">
					<div className="text-xs text-gray-500 mb-1">Joint Income</div>
					<div className="text-lg font-bold text-green-700">{formatCurrency(data.jointIncome || 0)}</div>
				</div>
				<div className="bg-blue-50 p-3 rounded text-center">
					<div className="text-xs text-gray-500 mb-1">Is Commercial</div>
					<span className={`px-2 py-1 rounded text-xs font-bold ${data.isCommercial ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
						{data.isCommercial ? 'YES' : 'NO'}
					</span>
				</div>
			</div>

			<div className="space-y-4">
				{Object.entries(applicantIncomes).map(([nationalId, income]: [string, any]) => (
					<div key={nationalId} className="border rounded-lg p-4 bg-gray-50">
						<h4 className="font-semibold text-gray-800 mb-3 text-sm">{nationalId}</h4>

						<div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
							<div className="bg-white p-2 rounded border text-center text-xs">
								<div className="text-gray-500">Base Income</div>
								<div className="font-semibold">{formatCurrency(income.baseIncome)}</div>
							</div>
							<div className="bg-white p-2 rounded border text-center text-xs">
								<div className="text-gray-500">Additional Income</div>
								<div className="font-semibold">{formatCurrency(income.additionalIncome)}</div>
							</div>
							<div className="bg-white p-2 rounded border text-center text-xs">
								<div className="text-gray-500">Total Income</div>
								<div className="font-semibold text-green-700">{formatCurrency(income.totalIncome)}</div>
							</div>
						</div>

						{/* Commercial Income Breakdown */}
						{income.commercialIncomeBreakdown && (
							<div>
								<div className="text-xs font-semibold text-gray-500 mb-2">Commercial Income Sources</div>
								<div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
									<div className="bg-white p-2 rounded border text-xs text-center">
										<div className="text-gray-500">Salaried</div>
										<div className="font-semibold">{formatCurrency(income.commercialIncomeBreakdown.salariedIncome)}</div>
									</div>
									<div className="bg-white p-2 rounded border text-xs text-center">
										<div className="text-gray-500">Self-Employed</div>
										<div className="font-semibold">{formatCurrency(income.commercialIncomeBreakdown.selfEmployedIncome)}</div>
									</div>
									<div className="bg-white p-2 rounded border text-xs text-center">
										<div className="text-gray-500">Real Estate Yield</div>
										<div className="font-semibold">{formatCurrency(income.commercialIncomeBreakdown.realEstateYieldIncome)}</div>
									</div>
								</div>

								{income.commercialIncomeBreakdown.sources && income.commercialIncomeBreakdown.sources.length > 0 && (
									<details className="bg-white rounded border p-2 text-xs">
										<summary className="cursor-pointer font-medium text-gray-700">
											Source Details ({income.commercialIncomeBreakdown.sources.length})
										</summary>
										<div className="mt-2 space-y-2">
											{income.commercialIncomeBreakdown.sources.map((source: any, idx: number) => (
												<div key={idx} className="border rounded p-2 bg-gray-50">
													<div className="flex justify-between items-center mb-1">
														<span className="font-medium">{source.source}</span>
														<div className="text-right">
															<span className="font-semibold">{formatCurrency(source.amount)}</span>
															<span className="text-gray-400 ml-2">({formatPercentage(source.percentage)})</span>
														</div>
													</div>
													{source.profileBreakdown && (
														<div className="grid grid-cols-2 gap-1 mt-1 text-[11px] text-gray-600">
															<div>Valid Credits: {formatCurrency(source.profileBreakdown.totalValidCredits)}</div>
															<div>Valid Txn Count: {source.profileBreakdown.validTransactionCount}</div>
															<div>Monthly Avg: {formatCurrency(source.profileBreakdown.monthlyAverage)}</div>
															<div>Excluded: {formatCurrency(source.profileBreakdown.totalExcludedAmount)}</div>
														</div>
													)}
												</div>
											))}
										</div>
									</details>
								)}
							</div>
						)}
					</div>
				))}
			</div>
		</div>
	);
};

// ==================== REDF CALCULATION SECTION ====================
const RedfCalculationSection: React.FC<{ stage: any }> = ({ stage }) => {
	const data = stage?.outputResult?.data;
	if (!data?.redfResults) return null;

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">REDF Calculation</h3>
			<div className="space-y-4">
				{Object.entries(data.redfResults).map(([nationalId, result]: [string, any]) => (
					<div key={nationalId} className="border rounded-lg p-4 bg-gray-50">
						<div className="flex items-center justify-between mb-3">
							<h4 className="font-semibold text-gray-800 text-sm">{nationalId}</h4>
							<span className={`px-2 py-1 rounded text-xs font-bold ${result.isEligible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
								{result.isEligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'}
							</span>
						</div>

						<div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Fund Value</div>
								<div className="text-lg font-bold text-green-600">{formatCurrency(result.fundValue)}</div>
							</div>
							<div className="bg-white p-3 rounded border text-center">
								<div className="text-xs text-gray-500">Guarantee Value</div>
								<div className="text-lg font-bold text-blue-600">{formatCurrency(result.guaranteeValue)}</div>
							</div>
						</div>

						{result.redfCalculation && (
							<details className="bg-white rounded border p-2 text-xs">
								<summary className="cursor-pointer font-medium text-gray-700">REDF Calculation Details</summary>
								<div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
									<div className="p-1"><span className="text-gray-500">Salary:</span> {formatCurrency(result.redfCalculation.salary)}</div>
									<div className="p-1"><span className="text-gray-500">Property Price:</span> {formatCurrency(result.redfCalculation.propertyPrice)}</div>
									<div className="p-1"><span className="text-gray-500">Down Payment:</span> {formatCurrency(result.redfCalculation.downPayment)}</div>
									<div className="p-1"><span className="text-gray-500">Period:</span> {result.redfCalculation.periodInMonths} months</div>
									<div className="p-1"><span className="text-gray-500">Profit Rate:</span> {formatPercentage(result.redfCalculation.profitRate * 100)}</div>
									<div className="p-1"><span className="text-gray-500">Total Repay:</span> {formatCurrency(result.redfCalculation.totalRepayAmount)}</div>
								</div>
							</details>
						)}
					</div>
				))}
			</div>
		</div>
	);
};

// ==================== REPAYMENT CALCULATIONS SECTION ====================
const RepaymentCalculationsSection: React.FC<{ stage: any }> = ({ stage }) => {
	const data = stage?.outputResult?.data;
	if (!data) return null;

	const rc = data.repaymentCalculations;

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Repayment Calculations</h3>

			<div className="flex items-center gap-2 mb-4">
				<span className="text-sm text-gray-500">DBR Check:</span>
				<span className={`px-2 py-1 rounded text-xs font-bold ${data.passedDBRCheck ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
					{data.passedDBRCheck ? 'PASSED' : 'FAILED'}
				</span>
			</div>

			{rc && (
				<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
					<div className="bg-blue-50 p-3 rounded text-center">
						<div className="text-xs text-gray-500 mb-1">Finance Amount</div>
						<div className="text-lg font-bold text-blue-700">{formatCurrency(rc.financeAmount)}</div>
					</div>
					<div className="bg-green-50 p-3 rounded text-center">
						<div className="text-xs text-gray-500 mb-1">Finance + Profit</div>
						<div className="text-lg font-bold text-green-700">{formatCurrency(rc.financeAmountIncludingProfit)}</div>
					</div>
					<div className="bg-purple-50 p-3 rounded text-center">
						<div className="text-xs text-gray-500 mb-1">Monthly EMI</div>
						<div className="text-lg font-bold text-purple-700">{formatCurrency(rc.emi)}</div>
					</div>
					<div className="bg-orange-50 p-3 rounded text-center">
						<div className="text-xs text-gray-500 mb-1">Tenor</div>
						<div className="text-lg font-bold text-orange-700">{rc.tenor} months</div>
					</div>
				</div>
			)}
		</div>
	);
};

// ==================== COMMERCIAL RESULTS DISPLAY ====================
const CommercialResultsDisplay: React.FC<{ result: JointApplicationResponse }> = ({ result }) => {
	const application = result.application;
	const stages = result.stages || {};

	return (
		<div className="space-y-6">
			{/* Reuse existing ResultsDisplay for common stages, but also render commercial-specific ones */}
			<ResultsDisplay result={result} />

			{/* Commercial Open Banking (if not already rendered by ResultsDisplay) */}
			{stages.COMMERCIAL_OPEN_BANKING && !stages.OPEN_BANKING && (
				<CommercialOpenBankingSection stage={stages.COMMERCIAL_OPEN_BANKING} application={application} />
			)}

			{/* Commercial RAC Policy (if not already rendered by ResultsDisplay) */}
			{stages.COMMERCIAL_RAC_POLICY && !stages.RAC_POLICY && (
				<CommercialRacPolicySection stage={stages.COMMERCIAL_RAC_POLICY} />
			)}

			{/* Unified Income */}
			{stages.UNIFIED_INCOME && (
				<UnifiedIncomeSection stage={stages.UNIFIED_INCOME} />
			)}

			{/* Commercial Scorecard (if not already rendered by ResultsDisplay) */}
			{stages.COMMERCIAL_SCORECARD_EVALUATION && !stages.SCORECARD_EVALUATION && (
				<CommercialScorecardSection stage={stages.COMMERCIAL_SCORECARD_EVALUATION} />
			)}

			{/* REDF Calculation */}
			{stages.REDF_CALCULATION && (
				<RedfCalculationSection stage={stages.REDF_CALCULATION} />
			)}

			{/* Repayment Calculations */}
			{stages.REPAYMENT_CALCULATIONS && (
				<RepaymentCalculationsSection stage={stages.REPAYMENT_CALCULATIONS} />
			)}
		</div>
	);
};

// ==================== COMMERCIAL SCORECARD SECTION ====================
const CommercialScorecardSection: React.FC<{ stage: any }> = ({ stage }) => {
	if (!stage?.outputResult?.data?.scorecards) return null;
	const scorecards = stage.outputResult.data.scorecards;

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Commercial Scorecard Evaluation</h3>
			<div className="space-y-6">
				{Object.entries(scorecards).map(([nationalId, data]: [string, any]) => (
					<div key={nationalId} className="border rounded-lg p-4 bg-gray-50">
						<div className="flex justify-between items-center mb-4">
							<h4 className="font-semibold text-gray-800">{nationalId}</h4>
							<div className="flex items-center space-x-2">
								<span className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
									Score: {data.score}
								</span>
								<span
									className={`text-xs font-semibold px-3 py-1 rounded-full ${
										data.riskGrade === 'Good' || data.riskGrade === 'Acceptable'
											? 'bg-green-100 text-green-800'
											: 'bg-yellow-100 text-yellow-800'
									}`}>
									Grade: {data.riskGrade}
								</span>
							</div>
						</div>

						<div className="overflow-x-auto">
							<table className="min-w-full text-xs text-left">
								<thead className="bg-gray-100 text-gray-600 uppercase font-medium">
									<tr>
										<th className="p-2 border-b">Attribute</th>
										<th className="p-2 border-b">Value</th>
										<th className="p-2 border-b text-center">Matched</th>
										<th className="p-2 border-b text-right">Points</th>
										<th className="p-2 border-b text-right">Weight</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-200 bg-white">
									{data.attributes?.map((attr: any, idx: number) => (
										<tr key={idx} className="hover:bg-gray-50 transition-colors">
											<td className="p-2 text-gray-700 font-medium whitespace-nowrap">{attr.attributeName}</td>
											<td className="p-2 text-gray-600 font-mono">{attr.value !== undefined ? String(attr.value) : '-'}</td>
											<td className="p-2 text-center">
												{attr.matched ? <span className="text-green-500 font-bold">&#10003;</span> : <span className="text-gray-300">-</span>}
											</td>
											<td className="p-2 text-right font-semibold text-blue-600">{attr.factorPoints}</td>
											<td className="p-2 text-right text-gray-500">{attr.weight}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

// ==================== MAIN COMPONENT ====================
interface CommercialApplicationFormProps {
	baseUrl: string;
}

const CommercialApplicationForm: React.FC<CommercialApplicationFormProps> = ({ baseUrl }) => {
	const [applicant, setApplicant] = useState<CommercialPrimaryApplicant>({ ...INITIAL_PRIMARY_APPLICANT });
	const [property, setProperty] = useState<CommercialProperty>({ ...INITIAL_PROPERTY, propertyRentUnits: [] });
	const [financialParams, setFinancialParams] = useState<CommercialRequestedFinancialParameters>({ ...INITIAL_FINANCIAL_PARAMS });
	const [ownershipType, setOwnershipType] = useState<OwnershipTypeEnum>(OwnershipTypeEnum.SOLE);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');
	const [initialResponse, setInitialResponse] = useState<CommercialApplicationResponse | null>(null);
	const [submittedRentUnits, setSubmittedRentUnits] = useState<PropertyRentUnit[]>([]);
	const [fullResult, setFullResult] = useState<JointApplicationResponse | null>(null);
	const [applicationId, setApplicationId] = useState('');
	const [isPolling, setIsPolling] = useState(false);

	const updateApplicant = <K extends keyof CommercialPrimaryApplicant>(field: K, value: CommercialPrimaryApplicant[K]) => {
		setApplicant((prev) => ({ ...prev, [field]: value }));
	};

	const updateExpenses = <K extends keyof Expenses>(field: K, value: number) => {
		setApplicant((prev) => ({
			...prev,
			expenses: { ...prev.expenses, [field]: value },
		}));
	};

	const updateProperty = <K extends keyof CommercialProperty>(field: K, value: CommercialProperty[K]) => {
		setProperty((prev) => ({ ...prev, [field]: value }));
	};

	const addRentUnit = () => {
		setProperty((prev) => ({ ...prev, propertyRentUnits: [...prev.propertyRentUnits, createEmptyRentUnit()] }));
	};

	const removeRentUnit = (index: number) => {
		setProperty((prev) => ({
			...prev,
			propertyRentUnits: prev.propertyRentUnits.filter((_, i) => i !== index),
		}));
	};

	const updateRentUnit = <K extends keyof PropertyRentUnit>(index: number, field: K, value: PropertyRentUnit[K]) => {
		setProperty((prev) => ({
			...prev,
			propertyRentUnits: prev.propertyRentUnits.map((unit, i) => (i === index ? { ...unit, [field]: value } : unit)),
		}));
	};

	const updateFinancialParams = <K extends keyof CommercialRequestedFinancialParameters>(
		field: K,
		value: CommercialRequestedFinancialParameters[K]
	) => {
		setFinancialParams((prev) => ({ ...prev, [field]: value }));
	};

	const toggleSourceOfIncome = (source: SourceOfIncomeEnum) => {
		setApplicant((prev) => {
			const current = prev.sourceOfIncome;
			if (current.includes(source)) {
				return { ...prev, sourceOfIncome: current.filter((s) => s !== source) };
			}
			return { ...prev, sourceOfIncome: [...current, source] };
		});
	};

	const pollApplicationStatus = useCallback(
		async (appId: string) => {
			try {
				const response = await apiFetch(`${baseUrl}/api/v1/application/get-application/${appId}`);
				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}
				const data = await response.json();
				if (data.success && data.data) {
					const applicationData: JointApplicationResponse = data.data;
					setFullResult(applicationData);
					setIsPolling(false);
				}
			} catch (error) {
				console.error('Error polling application status:', error);
			}
		},
		[baseUrl]
	);

	useEffect(() => {
		if (!isPolling || !applicationId) return;
		const interval = setInterval(() => pollApplicationStatus(applicationId), 2000);
		return () => clearInterval(interval);
	}, [isPolling, applicationId, pollApplicationStatus]);

	const handleSubmit = async () => {
		if (!applicant.nationalId) {
			setError('Please enter national ID for the primary applicant');
			return;
		}

		const { ownershipPercentage, downPayment } = financialParams;

		// Ownership percentage validation (mirrors backend VALIDATION_ERROR rules)
		if (ownershipPercentage < 0 || ownershipPercentage > 100) {
			setError('Ownership percentage must be between 0 and 100');
			return;
		}
		if (ownershipType === OwnershipTypeEnum.SOLE && ownershipPercentage !== 100) {
			setError('For SOLE ownership, ownership percentage must be exactly 100');
			return;
		}
		if (ownershipType === OwnershipTypeEnum.JOINT && ownershipPercentage >= 100) {
			setError('For JOINT ownership, ownership percentage must be less than 100');
			return;
		}

		// Down payment cannot exceed the owned share of the property price
		const ownedShare = property.price * (ownershipPercentage / 100);
		if (downPayment > ownedShare) {
			setError(
				`Down payment (${formatCurrency(downPayment)}) cannot exceed the owned share of the property price (${formatCurrency(
					ownedShare
				)})`
			);
			return;
		}

		setIsLoading(true);
		setError('');
		setInitialResponse(null);
		setFullResult(null);
		setApplicationId('');
		setIsPolling(false);

		try {
			const payload: CommercialApplicationData = {
				ownershipType,
				primaryApplicant: applicant,
				property,
				requestedFinancialParameters: financialParams,
			};

			const response = await apiFetch(`${baseUrl}/api/v1/application/commercial`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(payload),
			});

			if (!response.ok) {
				const errData = await response.json().catch(() => null);
				throw new Error(errData?.message || `HTTP error! status: ${response.status}`);
			}

			const data = await response.json();
			if (data.success && data.data && data.data.applicationId) {
				setInitialResponse(data.data as CommercialApplicationResponse);
				setSubmittedRentUnits(property.propertyRentUnits);
				setApplicationId(data.data.applicationId);
				setIsPolling(true);
			} else {
				throw new Error(data.message || 'Failed to submit commercial application');
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to submit application');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="space-y-6">
			{/* Primary Applicant */}
			<div className="bg-white p-4 rounded-lg shadow-sm border">
				<h3 className="text-lg font-semibold text-gray-800 mb-4">Primary Applicant</h3>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<InputField
						label="National ID"
						type="text"
						value={applicant.nationalId}
						onChange={(v) => updateApplicant('nationalId', v)}
						placeholder="10 digits"
					/>
					<InputField label="Profile ID" type="text" value={applicant.profileId} onChange={(v) => updateApplicant('profileId', v)} />
					<InputField label="First Name" type="text" value={applicant.firstName} onChange={(v) => updateApplicant('firstName', v)} />
					<InputField label="Last Name" type="text" value={applicant.lastName} onChange={(v) => updateApplicant('lastName', v)} />
					<InputField
						label="Nationality"
						type="text"
						value={applicant.nationality}
						onChange={(v) => updateApplicant('nationality', v)}
					/>
					<InputField
						label="Mobile Number"
						type="text"
						value={applicant.mobileNumber}
						onChange={(v) => updateApplicant('mobileNumber', v)}
						placeholder="+966..."
					/>
					<InputField
						label="Date of Birth (Gregorian)"
						type="date"
						value={applicant.personalInformation.dateOfBirth.gregorian}
						onChange={(v) =>
							setApplicant((prev) => ({
								...prev,
								personalInformation: {
									...prev.personalInformation,
									dateOfBirth: { ...prev.personalInformation.dateOfBirth, gregorian: v },
								},
							}))
						}
					/>
					<InputField
						label="Date of Birth (Hijri)"
						type="text"
						value={applicant.personalInformation.dateOfBirth.hijri || ''}
						onChange={(v) =>
							setApplicant((prev) => ({
								...prev,
								personalInformation: {
									...prev.personalInformation,
									dateOfBirth: { ...prev.personalInformation.dateOfBirth, hijri: v },
								},
							}))
						}
						placeholder="YYYY-MM-DD"
					/>
					<InputField
						label="Gender"
						type="select"
						value={applicant.gender}
						onChange={(v) => updateApplicant('gender', v as GenderEnum)}
						options={FORM_OPTIONS.GENDER}
					/>
					<InputField
						label="Marital Status"
						type="select"
						value={applicant.maritalStatus}
						onChange={(v) => updateApplicant('maritalStatus', v as MaritalStatusEnum)}
						options={FORM_OPTIONS.MARITAL_STATUS}
					/>
					<InputField
						label="Residential Type"
						type="select"
						value={applicant.residentialType}
						onChange={(v) => updateApplicant('residentialType', v as ResidentialTypeEnum)}
						options={FORM_OPTIONS.RESIDENTIAL_TYPE}
					/>
					<InputField
						label="Persona"
						type="select"
						value={applicant.persona}
						onChange={(v) => updateApplicant('persona', v as PersonaEnum)}
						options={FORM_OPTIONS.PERSONA}
					/>
					<InputField
						label="Sector"
						type="select"
						value={applicant.sector}
						onChange={(v) => updateApplicant('sector', v as SectorEnum)}
						options={FORM_OPTIONS.SECTOR}
					/>
					<InputField
						label="Educational Level"
						type="select"
						value={applicant.educationalLevel}
						onChange={(v) => updateApplicant('educationalLevel', v as EducationalLevelEnum)}
						options={FORM_OPTIONS.EDUCATIONAL_LEVEL}
					/>
					<InputField
						label="Number of Dependents"
						type="number"
						value={applicant.numberOfDependents}
						onChange={(v) => updateApplicant('numberOfDependents', v)}
					/>
					<InputField
						label="Dependents in School"
						type="number"
						value={applicant.numberOfDependentsInSchool}
						onChange={(v) => updateApplicant('numberOfDependentsInSchool', v)}
					/>
					<InputField
						label="Number of Cars Owned"
						type="number"
						value={applicant.numberOfCarsOwned}
						onChange={(v) => updateApplicant('numberOfCarsOwned', v)}
					/>
					<InputField
						label="RE Portfolio Size"
						type="number"
						value={applicant.rePortfolioSize}
						onChange={(v) => updateApplicant('rePortfolioSize', v)}
					/>

					{/* Rental Assignment */}
					<div className="flex items-center gap-2 mt-2">
						<label className="flex items-center gap-2 cursor-pointer">
							<input
								type="checkbox"
								checked={applicant.rentalAssignment}
								onChange={(e) => updateApplicant('rentalAssignment', e.target.checked)}
								className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
							/>
							<span className="text-sm font-medium text-gray-700">Rental Assignment</span>
						</label>
					</div>

					{/* Source of Income - Multi-select checkboxes */}
					<div className="col-span-2">
						<label className="block text-sm font-medium text-gray-700 mb-2">Source of Income</label>
						<div className="flex flex-wrap gap-3">
							{FORM_OPTIONS.SOURCE_OF_INCOME.map((source) => (
								<label key={source} className="flex items-center gap-2 cursor-pointer">
									<input
										type="checkbox"
										checked={applicant.sourceOfIncome.includes(source)}
										onChange={() => toggleSourceOfIncome(source)}
										className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
									/>
									<span className="text-sm text-gray-700">{source}</span>
								</label>
							))}
						</div>
					</div>

					{/* Expenses - Collapsible */}
					<div className="col-span-2">
						<details className="mt-2">
							<summary className="cursor-pointer font-medium text-gray-700 text-sm mb-3">Monthly Expenses</summary>
							<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
								<InputField label="Rent" type="number" value={applicant.expenses.rent} onChange={(v) => updateExpenses('rent', v)} />
								<InputField
									label="Food & Beverages"
									type="number"
									value={applicant.expenses.foodAndBeverages}
									onChange={(v) => updateExpenses('foodAndBeverages', v)}
								/>
								<InputField label="Education" type="number" value={applicant.expenses.education} onChange={(v) => updateExpenses('education', v)} />
								<InputField label="Health Care" type="number" value={applicant.expenses.healthCare} onChange={(v) => updateExpenses('healthCare', v)} />
								<InputField label="Utilities" type="number" value={applicant.expenses.utilities} onChange={(v) => updateExpenses('utilities', v)} />
								<InputField
									label="Transportation"
									type="number"
									value={applicant.expenses.transportation}
									onChange={(v) => updateExpenses('transportation', v)}
								/>
								<InputField label="Insurance" type="number" value={applicant.expenses.insurance} onChange={(v) => updateExpenses('insurance', v)} />
								<InputField
									label="Future Expenses"
									type="number"
									value={applicant.expenses.futureExpenses}
									onChange={(v) => updateExpenses('futureExpenses', v)}
								/>
								<InputField label="Others" type="number" value={applicant.expenses.others} onChange={(v) => updateExpenses('others', v)} />
							</div>
						</details>
					</div>
				</div>
			</div>

			{/* Property Information */}
			<div className="bg-white p-4 rounded-lg shadow-sm border">
				<h3 className="text-lg font-semibold text-gray-800 mb-4">Property Information</h3>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<InputField label="Property ID" type="text" value={property.propertyId} onChange={(v) => updateProperty('propertyId', v)} />
					<InputField
						label="Product Type"
						type="select"
						value={property.productType}
						onChange={(v) => updateProperty('productType', v as CommercialProductTypeEnum)}
						options={FORM_OPTIONS.COMMERCIAL_PRODUCT_TYPE}
					/>
					<InputField label="Price (SAR)" type="number" value={property.price} onChange={(v) => updateProperty('price', v)} />
					<InputField
						label="Region"
						type="select"
						value={property.region}
						onChange={(v) => updateProperty('region', v as RegionEnum)}
						options={FORM_OPTIONS.REGION}
					/>
				</div>

				{/* Property Rent Units */}
				<div className="mt-6 border-t pt-4">
					<div className="flex items-center justify-between mb-2">
						<h4 className="text-sm font-semibold text-gray-700">Property Rent Units</h4>
						<button
							type="button"
							onClick={addRentUnit}
							className="bg-blue-600 text-white text-xs font-medium py-1.5 px-3 rounded-md hover:bg-blue-700 transition-colors">
							+ Add Rent Unit
						</button>
					</div>
					<p className="text-xs text-gray-500 mb-3">
						Only non-expired units (end date on or after today) are counted. Annual rent is split by ownership percentage;
						60% of the monthly ownership share is added to the primary applicant's income. The frequency holding the most
						total annual income determines billing (Monthly &rarr; monthly installment; Quarterly or longer &rarr; quarterly
						installment).
					</p>

					{property.propertyRentUnits.length === 0 ? (
						<p className="text-xs text-gray-400 italic">No rent units added.</p>
					) : (
						<div className="space-y-3">
							{property.propertyRentUnits.map((unit, index) => (
								<div key={index} className="border rounded-lg p-3 bg-gray-50">
									<div className="flex items-center justify-between mb-2">
										<span className="text-xs font-semibold text-gray-600">Unit {index + 1}</span>
										<button
											type="button"
											onClick={() => removeRentUnit(index)}
											className="text-red-600 text-xs font-medium hover:text-red-800">
											Remove
										</button>
									</div>
									<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
										<InputField
											label="Start Date"
											type="date"
											value={unit.startDate}
											onChange={(v) => updateRentUnit(index, 'startDate', v)}
										/>
										<InputField
											label="End Date"
											type="date"
											value={unit.endDate}
											onChange={(v) => updateRentUnit(index, 'endDate', v)}
										/>
										<InputField
											label="Frequency"
											type="select"
											value={unit.frequency}
											onChange={(v) => updateRentUnit(index, 'frequency', v as RentFrequencyEnum)}
											options={FORM_OPTIONS.RENT_FREQUENCY}
										/>
										<InputField
											label="Income per Period (SAR)"
											type="number"
											value={unit.income}
											onChange={(v) => updateRentUnit(index, 'income', v)}
										/>
										<InputField
											label="Total Annual Income (SAR)"
											type="number"
											value={unit.totalAnnualIncome}
											onChange={(v) => updateRentUnit(index, 'totalAnnualIncome', v)}
										/>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			{/* Requested Financial Parameters */}
			<div className="bg-white p-4 rounded-lg shadow-sm border">
				<h3 className="text-lg font-semibold text-gray-800 mb-4">Requested Financial Parameters</h3>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<InputField
						label="Ownership Type"
						type="select"
						value={ownershipType}
						onChange={(v) => setOwnershipType(v as OwnershipTypeEnum)}
						options={FORM_OPTIONS.OWNERSHIP_TYPE}
					/>
					<InputField
						label="Ownership Percentage (0-100)"
						type="number"
						value={financialParams.ownershipPercentage}
						onChange={(v) => updateFinancialParams('ownershipPercentage', v)}
					/>
					<InputField
						label="Down Payment (SAR)"
						type="number"
						value={financialParams.downPayment}
						onChange={(v) => updateFinancialParams('downPayment', v)}
					/>
					<InputField
						label="Tenor (Months)"
						type="number"
						value={financialParams.tenor}
						onChange={(v) => updateFinancialParams('tenor', v)}
					/>
				</div>
				<p className="text-xs text-gray-500 mt-2">
					SOLE ownership requires exactly 100%. JOINT requires less than 100%. Down payment cannot exceed the owned share of
					the property price (price &times; ownership percentage). LTV is still assessed against the full property price.
				</p>
			</div>

			{/* Submit Button */}
			<div className="flex justify-center flex-col items-center gap-4">
				{error && <p className="text-red-600 text-sm font-medium">{error}</p>}
				{isPolling && applicationId && (
					<div className="text-center">
						<p className="text-gray-600 text-sm mb-1">
							Application ID: <span className="font-mono font-semibold">{applicationId}</span>
						</p>
						<p className="text-blue-600 text-sm animate-pulse">Processing commercial application...</p>
					</div>
				)}
				<button
					onClick={handleSubmit}
					disabled={isLoading || isPolling}
					className="bg-blue-600 text-white py-3 px-12 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-blue-400 transition-colors shadow-md">
					{isLoading ? 'Submitting...' : isPolling ? 'Processing...' : 'Submit Commercial Application'}
				</button>
			</div>

			{/* Initial Response */}
			{initialResponse && (
				<div className="mt-8 border-t-2 pt-6">
					<CommercialResponseDisplay
						response={initialResponse}
						installmentLabel={getInstallmentLabel(submittedRentUnits)}
					/>
				</div>
			)}

			{/* Full Application Results */}
			{fullResult && (
				<div className="mt-6">
					<div className="bg-gray-100 border-b border-gray-200 px-6 py-3 rounded-t-lg">
						<h3 className="text-lg font-semibold text-gray-800">Full Application Details (get-application)</h3>
					</div>
					<CommercialResultsDisplay result={fullResult} />

					{/* Raw JSON */}
					<div className="mt-6">
						<details className="bg-gray-100 rounded-lg p-4">
							<summary className="cursor-pointer font-medium text-gray-700">View Raw JSON Response</summary>
							<pre className="mt-4 text-xs overflow-auto max-h-96 bg-white p-4 rounded border">
								{JSON.stringify(fullResult, null, 2)}
							</pre>
						</details>
					</div>
				</div>
			)}
		</div>
	);
};

export default CommercialApplicationForm;
